import React from 'react'
import { Helmet } from 'react-helmet-async'
import VideoPresentation from './VideoDev.mp4'
import VideoPresentationPoster from './VideoDevPoster.png'
import SEO from '../../Hook/Seo'
import BreadcrumbsSchema from '../../Hook/BreadcrumbsSchema'
import InnovConnect from './innov_connect.png'
import AppelsShowcase from './AppelsShowcase'
import PlanningShowcase from './PlanningShowcase'
import CrmShowcase from './CrmShowcase'
import AdaptableProjectNote from './AdaptableProjectNote'
import ProjectApproach from './ProjectApproach'
import './Dev.css'

const structuredData = {
    '@context': 'https://schema.org',
    '@graph': [
        {
            '@type': 'Service',
            '@id': 'https://amservices.eu/dev#service',
            name: 'Développement web et applications métier sur mesure',
            url: 'https://amservices.eu/dev',
            description: 'Création de sites web, CRM, tableaux de bord et applications métier sur mesure pour les entreprises.',
            provider: { '@id': 'https://amservices.eu/#org' },
            areaServed: { '@type': 'AdministrativeArea', name: 'Vosges' },
            serviceType: ['Développement web', 'Application métier sur mesure', 'CRM sur mesure'],
        },
        {
            '@type': 'SoftwareApplication',
            '@id': 'https://taskpro.fr/#application',
            name: 'TaskPro',
            url: 'https://taskpro.fr',
            applicationCategory: 'BusinessApplication',
            operatingSystem: 'Web',
            description: 'Application métier de suivi visuel des dossiers, des priorités et de leur avancement, avec connexion Henrri.',
            creator: { '@id': 'https://amservices.eu/#org' },
        },
        {
            '@type': 'SoftwareApplication',
            '@id': 'https://appel.taskpro.fr/#application',
            name: 'Appels AM Services',
            url: 'https://appel.taskpro.fr',
            applicationCategory: 'BusinessApplication',
            operatingSystem: 'Web',
            description: 'Application métier de centralisation, affectation et suivi des appels de dépannage jusqu’à leur résolution.',
            creator: { '@id': 'https://amservices.eu/#org' },
        },
        {
            '@type': 'SoftwareApplication',
            '@id': 'https://planning.taskpro.fr/#application',
            name: 'Planning AM Services',
            url: 'https://planning.taskpro.fr',
            applicationCategory: 'BusinessApplication',
            operatingSystem: 'Web',
            description: 'Application métier de planification des interventions et d’affectation des techniciens avec vues semaine, mois et tableau.',
            creator: { '@id': 'https://amservices.eu/#org' },
        },
        {
            '@type': 'SoftwareApplication',
            '@id': 'https://crm.taskpro.fr/#application',
            name: 'CRM AM Services',
            url: 'https://crm.taskpro.fr',
            applicationCategory: 'BusinessApplication',
            operatingSystem: 'Web',
            description: 'Portail métier centralisant TaskPro, le suivi des appels et le planning des interventions dans une interface unique.',
            creator: { '@id': 'https://amservices.eu/#org' },
        },
    ],
};

const Dev = () => {
    const openApplicationRequest = (reference) => {
        const message = reference
            ? `Bonjour, je souhaite créer une application métier sur mesure similaire à ${reference}. Pouvez-vous me contacter pour échanger sur mon besoin ?`
            : "Bonjour, j’ai une idée de projet ou d’application sur mesure. Pouvez-vous me contacter pour étudier mon besoin avec moi ?";
        window.dispatchEvent(new CustomEvent('open-amservices-chat', {
            detail: {
                message
            }
        }));
    };

    return (
        <>
            <SEO
                title="Développement web & applications métier | AM Services"
                description="AM Services conçoit des sites web, CRM et applications métier sur mesure. Découvrez TaskPro, Appels, Planning et notre portail CRM centralisé."
                path="/dev"
                image="/og-developpement-web.png"
                keywords={["développement web Vosges", "application métier", "CRM sur mesure", "portail métier", "TaskPro", "suivi des appels", "planning interventions", "Kanban entreprise"]}
            />
            <BreadcrumbsSchema />
            <Helmet>
                <script type="application/ld+json">{JSON.stringify(structuredData)}</script>
            </Helmet>
            <div className="logiciel-page-premium">
                
                {/* --- HERO VIDEO --- */}
                <div className="info-hero-container">
                    <div className="info-video-wrapper">
                        <video
                            src={VideoPresentation}
                            poster={VideoPresentationPoster}
                            autoPlay
                            muted
                            loop
                            preload="auto"
                            playsInline
                            className="info-background-video"
                            aria-label="Vidéo de présentation des services AM Services"
                        />
                    </div>
                    <div className="info-hero-overlay"></div>
                    <div className="info-hero-center-text">
                        <h1 className="info-title">DÉVELOPPEMENT WEB</h1>
                        <p className="info-lead">
                            Chez AM Services, vos sites et applications sont modernes, utiles et sécurisés.
                        </p>
                    </div>
                    <div className="scroll-indicator">
                        <span className="arrow"></span>
                    </div>
                </div>

                {/* --- SECTIONS DE PRÉSENTATION DES SITES --- */}
                <section className="info-showcase-section">
                    <div className="section-intro-text">
                        <h2>Nos Réalisations & Solutions Web</h2>
                        <p>Découvrez notre savoir-faire à travers les projets et applications développés par AM Services.</p>
                        <div className="info-accent-line"></div>
                    </div>

                    <ProjectApproach onContact={() => openApplicationRequest()} />

                    <div className="expertises-layouts-container">

                        {/* RÉALISATION AM SERVICES : TASKPRO */}
                        <article className="taskpro-showcase">
                            <div className="taskpro-showcase-copy">
                                <span className="taskpro-signature">Une réalisation AM Services</span>
                                <h3>TaskPro</h3>
                                <h4>De la première demande au dossier terminé, chaque activité reste sous contrôle.</h4>
                                <p>
                                    TaskPro centralise les clients, les devis et les interventions dans un Kanban métier clair.
                                    Les équipes visualisent les priorités, déplacent les dossiers selon leur avancement et
                                    peuvent connecter Henrri pour retrouver leurs données commerciales.
                                </p>
                                <ul>
                                    <li>Suivi visuel des dossiers</li>
                                    <li>Organisation par métier</li>
                                    <li>Connexion avec Henrri</li>
                                    <li>Finalisation et archivage</li>
                                </ul>
                                <AdaptableProjectNote />
                                <div className="taskpro-showcase-actions">
                                    <a className="taskpro-primary" href="https://taskpro.fr" target="_blank" rel="noopener noreferrer">
                                        Tester la démo <span aria-hidden="true">↗</span>
                                    </a>
                                    <button type="button" className="taskpro-secondary" onClick={() => openApplicationRequest('TaskPro')}>Créer mon application métier</button>
                                </div>
                            </div>

                            <a className="taskpro-window" href="https://taskpro.fr" target="_blank" rel="noopener noreferrer" aria-label="Ouvrir TaskPro">
                                <div className="taskpro-window-bar"><i></i><i></i><i></i><span>taskpro.fr</span></div>
                                <div className="taskpro-window-body">
                                    <div className="taskpro-demo-head">
                                        <div><small>Suivi de production</small><strong>Vue d’ensemble</strong></div>
                                        <b>+ Nouveau dossier</b>
                                    </div>
                                    <div className="taskpro-demo-board">
                                        <div className="taskpro-demo-column">
                                            <h5>Nouveau <span>2</span></h5>
                                            <div className="taskpro-demo-card blue"><strong>Studio Atlas</strong><small>Création d’un nouvel espace client</small></div>
                                            <div className="taskpro-demo-card red"><strong>Restaurant des Halles</strong><small>Intervention urgente</small></div>
                                        </div>
                                        <div className="taskpro-demo-column">
                                            <h5>En cours <span>1</span></h5>
                                            <div className="taskpro-demo-card yellow"><strong>Résidence Les Jardins</strong><small>Visite de maintenance</small></div>
                                        </div>
                                        <div className="taskpro-demo-column last">
                                            <h5>Terminé <span>2</span></h5>
                                            <div className="taskpro-demo-card green"><strong>Maison Lenoir</strong><small>Projet finalisé</small></div>
                                            <div className="taskpro-demo-card blue"><strong>Boutique Nova</strong><small>Mise en service terminée</small></div>
                                        </div>
                                    </div>
                                </div>
                                <em>Explorer TaskPro ↗</em>
                            </a>
                        </article>

                        {/* RÉALISATION AM SERVICES : SUIVI DES APPELS */}
                        <AppelsShowcase onApplicationRequest={() => openApplicationRequest('Appels AM Services')} />

                        {/* RÉALISATION AM SERVICES : PLANNING D’INTERVENTIONS */}
                        <PlanningShowcase onApplicationRequest={() => openApplicationRequest('Planning AM Services')} />

                        {/* RÉALISATION AM SERVICES : PORTAIL CRM */}
                        <CrmShowcase onApplicationRequest={() => openApplicationRequest('CRM AM Services')} />

                        {/* Sections conservées pour une prochaine mise en ligne. */}
                        {false && (
                        <>
                        {/* SITE 1 : APPLICATIONS MÉTIERS & CRM */}
                        <div className="expertise-row-card">
                            <div className="expertise-text-side">
                                <span className="row-tag">Gestion Interne</span>
                                <h3>Applications Métiers & CRM</h3>
                                <p>
                                    Nous développons des applications web et CRM sur-mesure pour centraliser et automatiser la gestion de votre activité. Du suivi des plannings à la gestion client, nous concevons des interfaces robustes et sécurisées adaptées à vos flux de travail quotidiens.
                                </p>
                                <button className="btn-expertise-action" onClick={() => window.location.href = '/contact'}>
                                    En savoir plus
                                </button>
                            </div>
                            <div className="expertise-image-side">
                                <img src={InnovConnect} alt="Applications Métiers" className="premium-showcase-img" />
                            </div>
                        </div>

                        {/* SITE 2 : SITES VITRINES PREMIUM (EFFET MIROIR) */}
                        <div className="expertise-row-card reverse-row">
                            <div className="expertise-text-side">
                                <span className="row-tag">Visibilité & SEO</span>
                                <h3>Portails Web & Vitrines</h3>
                                <p>
                                    Mettez en valeur l'image de marque de votre entreprise avec un site vitrine moderne et performant. Optimisés pour le référencement naturel (SEO) et entièrement responsives, nos sites internet offrent une navigation fluide pour capter vos futurs clients.
                                </p>
                                <button className="btn-expertise-action" onClick={() => window.location.href = '/contact'}>
                                    En savoir plus
                                </button>
                            </div>
                            <div className="expertise-image-side">
                                <img src={InnovConnect} alt="Sites Vitrines" className="premium-showcase-img" />
                            </div>
                        </div>

                        {/* SITE 3 : INTERFACES DE DATA-VISUALISATION */}
                        <div className="expertise-row-card">
                            <div className="expertise-text-side">
                                <span className="row-tag">Statistiques & Analyse</span>
                                <h3>Tableaux de Bord Dynamiques</h3>
                                <p>
                                    Pilotez votre PME grâce à des outils de datavisualisation clairs et réactifs. Nous créons des plateformes capables de centraliser des bases de données complexes pour restituer vos statistiques, bilans et indicateurs sous forme de graphiques interactifs.
                                </p>
                                <button className="btn-expertise-action" onClick={() => window.location.href = '/contact'}>
                                    En savoir plus
                                </button>
                            </div>
                            <div className="expertise-image-side">
                                <img src={InnovConnect} alt="Tableaux de bord" className="premium-showcase-img" />
                            </div>
                        </div>

                        </>
                        )}

                    </div>
                </section>
            </div>
        </>
    )
}

export default Dev;
